<template>
  <div v-if="readonly">
    <p class="ma-0 pa-0 mb-2 subtitle-2 blue-grey--text text--darken-1">
      {{ label }}
    </p>
    <v-chip
      color="readonly-filter-chip blue-grey lighten-3 blue-grey--text text--darken-3"
      small
      :ripple="false"
      >{{ valueText }}</v-chip
    >
  </div>
  <div v-else class="date-range-filter-container">
    <v-autocomplete
      class="caption white mb-2"
      dense
      hide-details
      open-on-clear
      outlined
      :data-tourid="`filter-select-${id}`"
      :items="options"
      :menu-props="{ offsetY: true, maxWidth: 430 }"
      :no-data-text="$t('error.no_data_available')"
      :placeholder="$t('menu.selection.select_an_option')"
      :ref="`filter-${id}`"
      :style="{ width: `${fixedWidth}px` }"
      :value="value"
      @change="onChangeFilter($event)"
    >
      <template v-slot:label>
        <div class="caption white px-1" data-form-type="other">
          {{ label }}
        </div>
      </template>

      <template v-slot:prepend-item>
        <div
          v-if="allowClear"
          class="caption font-weight-500 cursor-pointer ml-4 mt-2 mb-2 blue--text clear-button"
          @click="handleClearFilterOptions"
        >
          {{ $t('common.clear') }}
        </div>
      </template>

      <template v-slot:selection="{ item }">
        <div>
          <p class=" selection-item-container ma-0 pa-0">
            {{ getFilterSingleSelectionText(item) }}
          </p>
        </div>
      </template>

      <template v-slot:item="data">
        <v-list-item-content
          class="ml-n1"
          :title="getOptionDateRangeTooltip(data.item)"
        >
          <v-list-item-title
            class="mx-0 px-0 blue-grey--text text--darken-3 mb-1"
          >
            {{ data.item.text }}
          </v-list-item-title>

          <v-list-item-subtitle
            class="font-size-12 blue-grey--text d-flex align-center"
          >
            <v-icon
              v-if="data.item.subtitleIcon"
              color="blue-grey"
              size="12"
              class="me-1"
              >{{ data.item.subtitleIcon }}</v-icon
            >
            {{ data.item.subtitle }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>

      <template v-slot:append>
        <v-icon
          :class="{ 'ml-6': showDatePicker }"
          :color="isActive ? 'blue' : 'blue-grey darken-1'"
        >
          {{ isActive ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </v-icon>
      </template>
    </v-autocomplete>

    <DateRangePicker
      v-if="showDatePicker"
      class="date-range-picker"
      :allowedDates="allowedDates"
      :dates="dateValues"
      @change="
        $emit('change', {
          key: id,
          value,
          dates: $event,
          isDateFilter: true,
          commitMode: 'instant',
          source: 'user',
          shouldApply: true,
        })
      "
    />
  </div>
</template>

<script>
import { utils } from 'frontend-js-shared'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import DateRangePicker from '@/components/DateRangePicker/DateRangePicker.vue'

export default {
  name: 'DateRangeFilter',

  mixins: [trackingMixin],

  components: {
    DateRangePicker,
  },

  props: {
    readonly: Boolean,
    label: String,
    id: String,
    options: { type: Array, default: () => [] },
    value: String,
    // when custom date option is selected this helps to populate the initial value of date picker also in custom cases we show the dates selected instead of "custom" word in filter
    dateValues: { type: Array, default: () => [] },
    allowedDates: Function,
    allowClear: Boolean,
    fixedWidth: Number,
  },

  computed: {
    valueText() {
      return (
        this.options?.find?.(option => option?.value === this.value)?.text ?? ''
      )
    },

    showDatePicker() {
      return this.value === this.$constants.DATE_FILTER_OPTIONS.custom
    },

    dateHumanParsed() {
      const humanDates = []

      if (this.dateValues.length) {
        humanDates[0] = utils.dates.formatDate(
          utils.dates.getStartOfDay(this.dateValues[0]),
          'MMM DD, YYYY'
        )
        humanDates[1] = utils.dates.formatDate(
          utils.dates.getEndOfDay(this.dateValues[1]),
          'MMM DD, YYYY'
        )
        return humanDates.join(' - ')
      }
      return null
    },

    isActive() {
      const filterRef = this.$refs?.[`filter-${this.id}`]
      return filterRef?.isMenuActive ?? false
    },
  },

  methods: {
    onChangeFilter(value) {
      const selectedOption = this.options.find(option => option.value === value)

      this.$emit('change', {
        dates: selectedOption?.dates || [],
        isDateFilter: true,
        key: this.id,
        value,
        commitMode: 'instant',
        source: 'user',
        shouldApply: true,
      })

      this.trackFilterActions('filters.changeFilterSelection', {
        name: this.label,
        value: this.value,
      })
    },

    handleDeleteFilter() {
      this.$emit('delete', this.id)
      this.trackFilterActions('filters.deleteFilter', {
        name: this.label,
      })
    },

    handleClearFilterOptions() {
      this.$emit('change', {
        key: this.id,
        value: null,
        isDateFilter: true,
        commitMode: 'instant',
        source: 'user',
        shouldApply: true,
      })
      this.trackFilterActions('filters.clearAllSelection', {
        name: this.label,
      })
    },

    getFilterSingleSelectionText(item) {
      return this.showDatePicker ? this.dateHumanParsed ?? item.text : item.text
    },

    getOptionDateRangeTooltip(item) {
      if ((item.dates?.length ?? 0) <= 0) {
        return null
      }

      const [startDate, endDate] = item.dates

      return `${utils.dates.formatDate(
        utils.dates.getStartOfDay(startDate),
        'MMM DD, YYYY'
      )} - ${utils.dates.formatDate(
        utils.dates.getEndOfDay(endDate),
        'MMM DD, YYYY'
      )}`
    },
  },
}
</script>

<style lang="scss" scoped>
.clear-button:hover {
  opacity: 0.7;
}
</style>

<style lang="scss">
.date-range-filter-container {
  position: relative;
  height: 32px !important;
  min-height: 32px !important;

  .v-input__control .v-input__slot {
    background-color: $color-white;
    height: 32px !important;
    min-height: 32px !important;

    fieldset legend {
      width: fit-content !important;
    }
    .v-select__slot {
      display: flex;
      align-items: center;
      .v-label {
        top: 8px;
        div {
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
      .v-label--active {
        top: 10px;
      }
      .v-select__selections:has(div) {
        input {
          width: 0px !important;
          min-width: 0px !important;
        }
      }
    }
    .v-input__append-inner {
      margin-top: 6px !important;
    }
  }

  .v-input__append-outer {
    background-color: $color-blue-grey-lighten3;
    margin: 0 !important;
    height: 32px;
    display: flex;
    align-items: center;
  }

  .selection-item-container {
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .date-picker {
    position: absolute !important;
    top: 0;
    bottom: 0;
    right: 36px;

    &::after {
      transform: none !important;
      background: none !important;
    }
  }
}
</style>
